import _extends from "@babel/runtime/helpers/esm/extends";
import { stack as d3Stack } from '@mui/x-charts-vendor/d3-shape';
import { warnOnce } from '@mui/x-internals/warning';
import { getStackingGroups } from "../../../internals/stacking/index.js";
const barValueFormatter = v => v == null ? '' : v.toLocaleString();
const seriesProcessor = (params, dataset, isItemVisible) => {
  const {
    seriesOrder,
    series
  } = params;
  const stackingGroups = getStackingGroups(params);

  // Create a data set with format adapted to d3
  const d3Dataset = dataset ?? [];
  seriesOrder.forEach(id => {
    const data = series[id].data;
    if (data !== undefined) {
      data.forEach((value, index) => {
        if (d3Dataset.length <= index) {
          d3Dataset.push({
            [id]: value
          });
        } else {
          d3Dataset[index][id] = value;
        }
      });
    } else if (dataset === undefined) {
      throw new Error([`MUI X Charts: bar series with id='${id}' has no data.`, 'Either provide a data property to the series or use the dataset prop.'].join('\n'));
    }
    if (process.env.NODE_ENV !== 'production') {
      if (!data && dataset) {
        const dataKey = series[id].dataKey;
        if (!dataKey) {
          throw new Error([`MUI X Charts: bar series with id='${id}' has no data and no dataKey.`, 'You must provide a dataKey when using the dataset prop.'].join('\n'));
        }
        dataset.forEach((entry, index) => {
          const value = entry[dataKey];
          if (value != null && typeof value !== 'number') {
            warnOnce([`MUI X Charts: your dataset key "${dataKey}" is used for plotting bars, but the dataset contains the non-null non-numerical element "${value}" at index ${index}.`, 'Bar plots only support numeric and null values.'].join('\n'));
          }
        });
      }
    }
  });
  const completedSeries = {};
  stackingGroups.forEach(stackingGroup => {
    const {
      ids,
      stackingOffset,
      stackingOrder
    } = stackingGroup;
    const keys = ids.map(id => {
      // Use dataKey if needed and available
      const dataKey = series[id].dataKey;
      return series[id].data === undefined && dataKey !== undefined ? dataKey : id;
    });
    const stackedData = d3Stack().keys(keys).value((d, key) => d[key] ?? 0) // defaultize null value to 0
    .order(stackingOrder).offset(stackingOffset)(d3Dataset);
    const idOrder = stackedData.map(s => s.index);
    const fixedOrder = () => idOrder;

    // Compute visible stacked data
    const visibleStackedData = d3Stack().keys(keys).value((d, key) => {
      const keyIndex = keys.indexOf(key);
      const seriesId = ids[keyIndex];
      if (!isItemVisible?.({
        type: 'bar',
        seriesId
      })) {
        // For hidden series, return 0 so they don't contribute to the stack
        return 0;
      }
      return d[key] ?? 0;
    }).order(fixedOrder).offset(stackingOffset)(d3Dataset);
    ids.forEach((id, index) => {
      const dataKey = series[id].dataKey;
      const data = dataKey ? dataset.map(d => {
        const value = d[dataKey];
        return typeof value === 'number' ? value : null;
      }) : series[id].data;
      const hidden = !isItemVisible?.({
        type: 'bar',
        seriesId: id
      });
      completedSeries[id] = _extends({
        layout: 'vertical',
        labelMarkType: 'square',
        minBarSize: 0,
        valueFormatter: series[id].valueFormatter ?? barValueFormatter
      }, series[id], {
        data,
        hidden,
        stackedData: stackedData[index],
        visibleStackedData: visibleStackedData[index]
      });
    });
  });
  return {
    seriesOrder,
    stackingGroups,
    series: completedSeries
  };
};
export default seriesProcessor;